/**
 * Referee api-client seam.
 *
 * One typed interface, two implementations:
 *   - `createMockClient()` — in-memory fixtures that drive the whole flow, so every screen is
 *     buildable and demoable before the endpoints land (WP-B11/B12/B14/B15).
 *   - `createHttpClient()` — real fetch against `VITE_API_BASE_URL`, referee token header +
 *     cookie session (NOT a JWT bearer).
 *
 * Selection is env-driven (see `useMocks()`), so screens import `apiClient` and never know which
 * backing they got. When `@bymyword/api-types` ships, the HTTP client re-points at the generated
 * wrapper and this interface aligns to it.
 */
import type {
  AudioCompleteRequest,
  AudioInitRequest,
  AudioUploadTicket,
  ConfirmDetailsRequest,
  ConsentAttestation,
  CorrectNameRequest,
  CorrectNameResult,
  DeclineRequest,
  DraftState,
  DraftWrite,
  EditDraftRequest,
  RefereeContext,
  LiveKeyPointsRequest,
  RealtimeSession,
  RegenerateDraftRequest,
  SignDisclosures,
  SignRequest,
  SignResult,
} from './api-types';
import { createMockClient } from './api-client.mock';
import { createHttpClient } from './api-client.http';

/** The tokenized-link session token, `selector.verifier`, as taken from the `/r/:token` route. */
export type RefereeToken = string;

export interface RefereeApiClient {
  /** Resolve the session context for a token. Drives step restoration + guards. */
  getContext(token: RefereeToken): Promise<RefereeContext>;
  /** Accept the request — mints the cookie-bound scoped session server-side. */
  accept(token: RefereeToken): Promise<RefereeContext>;
  /** Decline with an optional neutral reason. Terminal. */
  decline(token: RefereeToken, req?: DeclineRequest): Promise<RefereeContext>;
  /** Confirm the details that appear in the letter. */
  confirmDetails(token: RefereeToken, req: ConfirmDetailsRequest): Promise<RefereeContext>;
  /**
   * Record consent capture (recording/transcription/AI drafting). `consent` carries the current
   * attestation key + version surfaced by `/context`; when omitted the transport falls back to the
   * default document/version. The backend rejects a stale version (422 `consent_version_mismatch`).
   */
  recordConsent(token: RefereeToken, consent?: ConsentAttestation): Promise<void>;
  /** Exchange a WebRTC SDP offer without exposing the provider key; null when live notes are off. */
  createRealtimeSession(token: RefereeToken, offerSdp: string): Promise<RealtimeSession | null>;
  /** Refresh the provisional key-point preview from accumulated final transcript turns. */
  extractLiveKeyPoints(token: RefereeToken, req: LiveKeyPointsRequest): Promise<string[]>;
  /** Get a presigned single-PUT ticket for the recorded blob (called after stop()). */
  initAudioUpload(token: RefereeToken, req: AudioInitRequest): Promise<AudioUploadTicket>;
  /**
   * PUT the recorded blob to the presigned ticket — a SINGLE retryable constrained upload. The
   * mock fakes it (offline demo); the HTTP client does the real first-party R2 PUT. Kept on the
   * seam so the record step never touches raw fetch and the whole flow stays demoable without a
   * backend.
   */
  uploadAudio(ticket: AudioUploadTicket, blob: Blob): Promise<void>;
  /** Confirm the uploaded object (HEAD-verified server-side) and enqueue the pipeline. */
  completeAudioUpload(token: RefereeToken, req: AudioCompleteRequest): Promise<RefereeContext>;
  /** Poll the draft/pipeline state (processing → needs_review/rerecord/drafted). */
  pollDraft(token: RefereeToken): Promise<DraftState>;
  /**
   * Inline-edit the letter body (append-only version server-side). FREE and uncapped — an edit is
   * never a regeneration and never spends the budget, so editing stays live even once the cap is.
   */
  editDraft(token: RefereeToken, req: EditDraftRequest): Promise<DraftWrite>;
  /**
   * Re-draft the same key points in a different tone. Server-capped (the counter is derived from
   * the version chain, never taken from here); the cap surfaces as a 409 `regeneration_cap_reached`.
   */
  regenerateDraft(token: RefereeToken, req: RegenerateDraftRequest): Promise<DraftWrite>;
  /**
   * The pre-sign disclosures: the attestation wording, the version it is published under, and the
   * name the signature will be checked against. Fetched rather than authored — the ceremony must
   * show the exact wording the server will bind the signature to, and the exact name it will
   * compare. 409 `cannot_sign` when the request is not in a signable state.
   */
  getSignDisclosures(token: RefereeToken): Promise<SignDisclosures>;
  /**
   * Sign & send — atomic signature snapshot + verification code. Terminal, and IDEMPOTENT: a
   * repeat submission returns the ORIGINAL verification code with a 200, never a second signature
   * and never a conflict. Callers must therefore treat a resolved sign as success without asking
   * whether it was the first one.
   */
  sign(token: RefereeToken, req: SignRequest): Promise<SignResult>;
  /**
   * Correct the referee's own name before signing — the way out of a `name_mismatch` that isn't
   * "retype the typo". Pre-sign only: 409 `cannot_correct_name` once signed (the snapshot is
   * immutable). Answers `previousNameInLetter` when the replaced name is still in the letter body,
   * which is a prompt to review the draft — never a block, and the letter is never rewritten.
   */
  correctName(token: RefereeToken, req: CorrectNameRequest): Promise<CorrectNameResult>;
}

/**
 * Which backing to use.
 *   unset  → mocks in dev, HTTP in prod
 *   "true" → force mocks · "false" → force HTTP
 */
export function shouldUseMocks(): boolean {
  const flag = import.meta.env.VITE_USE_MOCKS;
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  return import.meta.env.DEV;
}

export const apiClient: RefereeApiClient = shouldUseMocks() ? createMockClient() : createHttpClient();

export { createMockClient, createHttpClient };
